import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/api";
import { prepararFoto } from "@/lib/imagem";
import ClienteShell from "@/components/ClienteShell";
import { ChatServico } from "@/components/ChatServico";
import { ProdutosLista } from "@/components/ProdutosLista";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, CalendarDays, Phone, User, CheckCircle2, RotateCcw, XCircle, AlertTriangle, Star, Package, ShieldCheck, ArrowRight, Camera } from "lucide-react";

const CATEGORIA_LABEL = {
  pre_montagem: "Pré-montagem",
  montagem: "Montagem",
  revisao: "Revisão",
  assistencia: "Assistência",
  video: "Vídeo",
};

const STATUS_TEXT = {
  aguardando_distribuicao: "Procurando técnico",
  em_microjanela: "Buscando montador na sua região",
  aguardando_disponibilidade: "Buscando montador na sua região",
  aguardando_disponibilidade_montadora: "Buscando montadora na sua região",
  aguardando_aceite_tecnico: "Aguardando aceite do técnico",
  aguardando_data_tecnico: "Técnico está escolhendo a data",
  aguardando_confirmacao_cliente: "Confirme a data proposta",
  agendado: "Agendado",
  em_atendimento: "Em atendimento",
  finalizado: "Finalizado",
  cancelado: "Cancelado",
  ausente: "Reagendamento necessário",
  impedido: "Atendimento impedido — em análise",
  aguardando_ordem: "Em aguardo. Entre em contato com a montadora.",
};

const TIMELINE = [
  { key: "pedido", label: "Pedido confirmado" },
  { key: "tecnico", label: "Técnico designado" },
  { key: "data", label: "Data agendada" },
  { key: "concluido", label: "Montagem concluída" },
];

function timelineIndex(status) {
  if (status === "finalizado") return 4;
  if (status === "agendado" || status === "em_atendimento") return 3;
  if (status === "aguardando_confirmacao_cliente" || status === "aguardando_data_tecnico") return 2;
  return 1;
}

export default function ClienteAcompanhar() {
  const { servicoId } = useParams();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(false);
  const [naoEncontrado, setNaoEncontrado] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/cliente/servico/${servicoId}`);
      setS(r.data);
    } catch (e) {
      setNaoEncontrado(true);
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, [servicoId]);

  const confirmar = async (data) => {
    setLoading(true);
    try {
      await axios.post(`${API}/cliente/confirmar-data/${servicoId}`, { data_escolhida: data });
      toast.success("Data confirmada!");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const recusarTodas = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/cliente/recusar-datas/${servicoId}`);
      if (r.data.status === "aguardando_ordem") {
        toast.success("Pedido em aguardo. Entre em contato com a montadora.");
      } else {
        toast.success("Reagendamento solicitado. Nova janela: D+7 a D+10");
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const cancelar = async () => {
    const motivo = prompt("Por que está cancelando?");
    if (!motivo) return;
    setLoading(true);
    try {
      const fd = new FormData(); fd.append("motivo", motivo);
      await axios.post(`${API}/cliente/cancelar/${servicoId}`, fd);
      toast.success("Atendimento cancelado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  if (naoEncontrado) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-6" data-testid="cliente-servico-nao-encontrado">
        <div className="text-center max-w-sm">
          <div className="font-display font-extrabold text-2xl text-[#0F172A]">Serviço não encontrado</div>
          <p className="text-sm text-[#64748B] mt-2">Verifique o link recebido ou busque novamente pelo seu CPF ou número da OS.</p>
          <Link to="/qrcode" className="btn-aj-primary inline-flex mt-6" data-testid="cliente-voltar-busca-btn">
            <ArrowLeft size={16} /> Voltar para a busca
          </Link>
        </div>
      </div>
    );
  }
  if (!s) return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center text-[#64748B]">Carregando…</div>;

  const tlIdx = timelineIndex(s.status);
  const cancelado = s.status === "cancelado";

  return (
    <ClienteShell
      step={s.status === "finalizado" ? 3 : 2}
      headerRight={
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs text-[#94A3B8]">{s.pim_code}</span>
          <Link to="/qrcode" className="text-sm font-semibold text-[#142766] hover:text-[#FF6700] transition-colors inline-flex items-center gap-1" data-testid="cliente-voltar">
            <ArrowLeft size={15} /> Voltar
          </Link>
        </div>
      }
    >
      <div className="aj-fade-up">
        <div className="aj-eyebrow">Status do pedido</div>
        <h1 className="font-display font-extrabold text-3xl text-[#0F172A] mt-2" data-testid="cliente-status-titulo">
          {STATUS_TEXT[s.status] || s.status}
        </h1>
      </div>

      {!cancelado && (
        <div className="mt-7 aj-card p-6 aj-fade-up-1" data-testid="cliente-timeline">
          <div className="flex items-start">
            {TIMELINE.map((t, i) => {
              const done = i < tlIdx;
              const current = i === tlIdx - 1 && tlIdx < 4;
              return (
                <React.Fragment key={t.key}>
                  <div className="flex flex-col items-center text-center flex-shrink-0 w-16 sm:w-20">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      done
                        ? current
                          ? "bg-gradient-to-br from-[#FF6700] to-[#F97316] text-white shadow-[0_4px_12px_-4px_rgba(234,88,12,.6)]"
                          : "bg-[#142766] text-white"
                        : "bg-[#F1F5F9] border border-[#E2E8F0] text-[#94A3B8]"
                    }`}>
                      {done && !current ? <CheckCircle2 size={16} /> : <span className="text-xs font-bold">{i + 1}</span>}
                    </div>
                    <span className={`mt-2 text-[10px] sm:text-[11px] font-semibold leading-tight ${done ? "text-[#0F172A]" : "text-[#94A3B8]"}`}>
                      {t.label}
                    </span>
                  </div>
                  {i < TIMELINE.length - 1 && (
                    <div className={`flex-1 h-[2px] mt-4 rounded ${i < tlIdx - 1 ? "bg-[#142766]" : "bg-[#E2E8F0]"}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-5 aj-card p-6 aj-fade-up-2">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#142766] mb-3">
          <Package size={14} /> Detalhes do pedido
        </div>
        <div className="grid gap-1.5 text-sm text-[#334155]">
          <div><span className="font-semibold text-[#142766]">Produto:</span> {s.produtos?.length > 1 ? `Pedido com ${s.produtos.length} itens` : s.produto}</div>
          <ProdutosLista produtos={s.produtos} />
          <div><span className="font-semibold text-[#142766]">Quantidade:</span> {s.quantidade}</div>
          <div><span className="font-semibold text-[#142766]">Endereço:</span> {s.cliente_endereco}</div>
        </div>
      </div>

      {s.status === "impedido" && s.ocorrencia_atual?.tipo === "produto_nao_entregue" && (
        <div className="mt-5 aj-card-elevated p-6 border-l-4 border-l-[#FF6700]" data-testid="produto-nao-entregue-bloco">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#E05C00] mb-2">
            <Package size={14} /> Produto ainda não entregue
          </div>
          {s.produto_chegou_em ? (
            <p className="text-sm text-[#334155]" data-testid="produto-chegou-ok">
              ✅ Você já avisou que o produto chegou. A loja foi notificada e a montagem será reagendada em breve.
            </p>
          ) : (
            <>
              <p className="text-sm text-[#334155]">
                Nosso montador esteve no local, mas o produto ainda não havia sido entregue.
                Assim que ele chegar, toque no botão abaixo para reagendarmos a montagem.
              </p>
              <button
                onClick={async () => {
                  try {
                    await axios.post(`${API}/cliente/servico/${servicoId}/produto-chegou`);
                    toast.success("Aviso enviado! A montagem será reagendada.");
                    load();
                  } catch (e) { toast.error(formatApiError(e)); }
                }}
                className="mt-4 w-full py-3 rounded-lg bg-[#FF6700] hover:bg-[#e05a00] text-white font-bold text-sm uppercase tracking-wider"
                data-testid="produto-chegou-btn">
                📦 MEU PRODUTO CHEGOU
              </button>
            </>
          )}
        </div>
      )}

      {s.status === "aguardando_confirmacao_cliente" && s.datas_propostas?.length > 0 && (
        <div className="mt-5 aj-fade-up-3" data-testid="cliente-data-unica">
          <div className="aj-card-elevated p-6 border-l-4 border-l-[#FF6700]">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#E05C00] mb-3">
              <CalendarDays size={14} /> Data proposta pelo técnico
            </div>
            <div className="font-display font-extrabold text-3xl text-[#0F172A]">{formatDate(s.datas_propostas[0])}</div>

            {s.montador && (
              <div className="mt-4 p-4 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] text-sm">
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-[#64748B] mb-2">
                  <User size={13} /> Técnico responsável
                </div>
                <div className="font-semibold text-[#0F172A]">{s.montador.nome}</div>
                <a href={`tel:${s.montador.telefone}`} className="inline-flex items-center gap-1.5 text-[#142766] font-semibold mt-1 hover:text-[#FF6700] transition-colors">
                  <Phone size={13} /> {s.montador.telefone}
                </a>
              </div>
            )}

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <button onClick={() => confirmar(s.datas_propostas[0])} disabled={loading}
                className="h-12 rounded-lg bg-[#10B981] text-white font-semibold text-sm inline-flex items-center justify-center gap-2 hover:brightness-105 transition-[filter] disabled:opacity-60"
                data-testid="cliente-confirmar">
                <CheckCircle2 size={16} /> Confirmar
              </button>
              <button onClick={recusarTodas} disabled={loading}
                className="h-12 rounded-lg border-[1.5px] border-[#142766] text-[#142766] font-semibold text-sm inline-flex items-center justify-center gap-2 hover:bg-[#142766] hover:text-white transition-colors disabled:opacity-60"
                data-testid="cliente-reagendar">
                <RotateCcw size={15} /> Reagendar
              </button>
              <button onClick={cancelar} disabled={loading}
                className="h-12 rounded-lg border-[1.5px] border-[#EF4444] text-[#EF4444] font-semibold text-sm inline-flex items-center justify-center gap-2 hover:bg-[#EF4444] hover:text-white transition-colors disabled:opacity-60"
                data-testid="cliente-cancelar">
                <XCircle size={15} /> Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {s.status === "agendado" && (
        <div className="mt-5 aj-card-elevated p-6 border-l-4 border-l-[#10B981] aj-fade-up-3" data-testid="cliente-agendado-card">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#059669] mb-2">
            <CheckCircle2 size={14} /> Agendado
          </div>
          <div className="font-display font-extrabold text-2xl text-[#0F172A]">{formatDate(s.data_confirmada)}</div>
          {s.montador && (
            <div className="mt-3 text-sm text-[#334155]">
              <div>Técnico: <strong>{s.montador.nome}</strong></div>
              <div className="inline-flex items-center gap-1.5 mt-0.5"><Phone size={13} className="text-[#142766]" /> {s.montador.telefone}</div>
            </div>
          )}
        </div>
      )}

      {s.status === "aguardando_ordem" && s.montadora && (
        <div className="mt-5 aj-card p-6 border-l-4 border-l-[#F59E0B] aj-fade-up-3" data-testid="cliente-aguardando-ordem">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#B45309] mb-2">
            <AlertTriangle size={14} /> Atenção
          </div>
          <p className="text-sm text-[#334155]">
            Sua montagem está em aguardo. Para reagendar, entre em contato com a montadora:
          </p>
          <div className="mt-3 font-mono text-sm text-[#0F172A]">
            WhatsApp: <strong>{s.montadora.whatsapp}</strong>
          </div>
        </div>
      )}

      {s.status === "finalizado" && (
        <Link to={`/cliente/posvenda/${s.id}`} className="block mt-6 aj-fade-up-3">
          <button className="btn-aj-primary w-full h-12 justify-center" data-testid="cliente-ir-pos-venda">
            <Star size={17} /> Avaliar atendimento
          </button>
        </Link>
      )}

      {["aguardando_distribuicao", "aguardando_aceite_tecnico", "aguardando_data_tecnico"].includes(s.status) && (s.datas_cliente || []).length > 0 && (
        <div className="mt-5 aj-card p-6 aj-fade-up-3" data-testid="cliente-datas-informadas">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#142766] mb-2">
            <CalendarDays size={14} /> Suas datas informadas
          </div>
          <p className="text-sm text-[#334155]">Um técnico da sua região confirmará o agendamento em uma destas datas:</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {s.datas_cliente.map((d) => (
              <span key={d} className="px-3 py-1.5 rounded-lg bg-[#F1F5F9] border border-[#E2E8F0] text-sm font-semibold text-[#0F172A]">
                {formatDate(d)}
              </span>
            ))}
          </div>
        </div>
      )}

      {(s.fotos || []).length > 0 && (
        <div className="mt-5 aj-card p-6 aj-fade-up-3" data-testid="cliente-fotos-card">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#142766] mb-3">
            <Camera size={14} /> Fotos e vídeos da montagem
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {s.fotos.map((ev) => {
              const url = `${API}/files/${s.id}/${ev.categoria}/${(ev.path || "").split("/").pop()}?s=${ev.sig || ""}`;
              const isVideo = (ev.content_type || "").startsWith("video");
              return (
                <a key={ev.id} href={url} target="_blank" rel="noreferrer"
                  className="block rounded-lg overflow-hidden border border-[#E2E8F0] hover:opacity-90 transition-opacity"
                  data-testid={`cliente-foto-${ev.id}`}>
                  {isVideo ? (
                    <video src={url} className="w-full h-24 object-cover bg-black" muted />
                  ) : (
                    <img src={url} alt={CATEGORIA_LABEL[ev.categoria] || ev.categoria}
                      className="w-full h-24 object-cover" loading="lazy" />
                  )}
                  <div className="px-1.5 py-1 text-[10px] text-[#64748B] font-semibold truncate">
                    {CATEGORIA_LABEL[ev.categoria] || ev.categoria}
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {s.tipo === "garantia" && (
        <div className="mt-5 aj-card p-6 border-l-4 border-l-[#142766]" data-testid="cliente-garantia-badge">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#142766] mb-2">
            <ShieldCheck size={14} /> Serviço de Garantia · sem custo
          </div>
          {s.garantia_descricao && (
            <p className="text-sm text-[#334155]">Problema relatado: "{s.garantia_descricao}"</p>
          )}
          {s.servico_original && (
            <Link to={`/cliente/servico/${s.servico_original.id}`}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#142766] hover:text-[#FF6700] transition-colors mt-2"
              data-testid="cliente-garantia-ver-original">
              Ver serviço original ({s.servico_original.pim_code}) <ArrowRight size={14} />
            </Link>
          )}
        </div>
      )}

      {s.status === "finalizado" && s.garantia_info?.elegivel && (
        <GarantiaCard servicoId={s.id} diasRestantes={s.garantia_info.dias_restantes} onEnviado={load} />
      )}

      {s.garantia_info?.motivo === "em_analise" && s.garantia_info.solicitacao && (
        <GarantiaEmAnaliseCard solic={s.garantia_info.solicitacao} />
      )}

      {s.garantia_info?.motivo === "complemento_solicitado" && s.garantia_info.solicitacao && (
        <GarantiaComplementoCard solic={s.garantia_info.solicitacao} onEnviado={load} />
      )}

      {s.garantia_info?.motivo === "recusada" && s.garantia_info.recusa && (
        <GarantiaRecusadaCard recusa={s.garantia_info.recusa} />
      )}

      {s.garantia_info?.motivo === "ja_acionada" && s.garantia_info.garantia && (
        <div className="mt-5 aj-card p-6 border-l-4 border-l-[#142766]" data-testid="cliente-garantia-acionada">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#142766] mb-2">
            <ShieldCheck size={14} /> Garantia acionada
          </div>
          <p className="text-sm text-[#334155]">
            Sua garantia <span className="font-mono font-bold">{s.garantia_info.garantia.pim_code}</span> está em andamento, sem custo.
          </p>
          <Link to={`/cliente/servico/${s.garantia_info.garantia.id}`}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#142766] hover:text-[#FF6700] transition-colors mt-2"
            data-testid="cliente-garantia-acompanhar">
            Acompanhar garantia <ArrowRight size={14} />
          </Link>
        </div>
      )}

      {/* Falar com a equipe interna */}
      {(s.empresa || s.montadora) && (
        <div className="mt-5 aj-card p-6" data-testid="cliente-falar-equipe">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#142766] mb-2">
            <Phone size={14} /> Falar com a equipe
          </div>
          <p className="text-sm text-[#334155]">
            Precisa cancelar, reagendar ou enviar informações? Use os botões acima ou fale direto com a equipe responsável:
          </p>
          <div className="mt-3 space-y-1.5 text-sm">
            {s.montadora?.whatsapp && (
              <div>
                Montadora <strong>{s.montadora.nome}</strong>:{" "}
                <a href={`https://wa.me/55${String(s.montadora.whatsapp).replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
                  className="font-semibold text-[#142766] hover:text-[#FF6700]" data-testid="cliente-contato-montadora">
                  WhatsApp {s.montadora.whatsapp}
                </a>
              </div>
            )}
            {s.empresa?.telefone && (
              <div>
                Loja <strong>{s.empresa.nome}</strong>:{" "}
                <a href={`tel:${s.empresa.telefone}`} className="font-semibold text-[#142766] hover:text-[#FF6700]"
                  data-testid="cliente-contato-empresa">{s.empresa.telefone}</a>
              </div>
            )}
            <div>
              Central MontaJá:{" "}
              <a href="https://wa.me/5511939387366" target="_blank" rel="noreferrer"
                className="font-semibold text-[#142766] hover:text-[#FF6700]" data-testid="cliente-contato-central">
                WhatsApp (11) 93938-7366
              </a>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6" data-testid="cliente-chat-wrap">
        <ChatServico meuTipo="cliente" titulo="Fale com nossa equipe (registrado)"
          somenteLeitura={["finalizado", "cancelado", "DEMO_ENCERRADA"].includes(s?.status)}
          listar={async () => (await axios.get(`${API}/cliente/servico/${servicoId}/chat`)).data}
          enviar={async (t) => axios.post(`${API}/cliente/servico/${servicoId}/chat`, { texto: t })} />
      </div>
    </ClienteShell>
  );
}

function GarantiaCard({ servicoId, diasRestantes, onEnviado }) {
  const [aberto, setAberto] = useState(false);
  const [descricao, setDescricao] = useState("");
  const [fotos, setFotos] = useState([]);
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(false);

  const onFotos = async (e) => {
    const arr = Array.from(e.target.files || []);
    if (arr.length > 5) {
      toast.error("Envie no máximo 5 fotos");
      return;
    }
    const invalidas = arr.filter((f) => !(f.type || "").startsWith("image/"));
    if (invalidas.length) {
      toast.error("Apenas imagens são aceitas nas fotos");
      return;
    }
    try {
      // Limite de 5MB é POR FOTO (original); cada uma é comprimida (~500KB) antes do envio
      const preparadas = [];
      for (const f of arr) preparadas.push(await prepararFoto(f));
      setFotos(preparadas);
    } catch (err) {
      toast.error(err?.userMessage || "Não foi possível processar as fotos");
      e.target.value = "";
    }
  };

  const onVideo = (e) => {
    const f = (e.target.files || [])[0] || null;
    if (f && !(f.type || "").startsWith("video/")) {
      toast.error("Envie um vídeo (MP4/MOV/WEBM)");
      return;
    }
    setVideo(f);
  };

  const enviar = async () => {
    if (descricao.trim().length < 10) {
      toast.error("Descreva o problema com pelo menos 10 caracteres");
      return;
    }
    if (fotos.length < 1) {
      toast.error("Envie pelo menos 1 foto do problema");
      return;
    }
    if (!video) {
      toast.error("Envie 1 vídeo do problema");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("descricao", descricao);
      fotos.forEach((f) => fd.append("fotos", f));
      fd.append("video", video);
      const r = await axios.post(`${API}/cliente/garantia/${servicoId}/solicitar`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(r.data.mensagem || "Reclamação registrada. Nossa equipe retornará em breve.");
      if (r.data.avaliacao_token) window.location.assign(`/cliente/avaliacao/${r.data.avaliacao_token}`);
      setAberto(false);
      setDescricao("");
      setFotos([]);
      setVideo(null);
      if (onEnviado) onEnviado();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="mt-5 aj-card p-6 border-l-4 border-l-[#F59E0B]" data-testid="cliente-garantia-card">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#B45309] mb-2">
        <ShieldCheck size={14} /> Reclamação pós-montagem · Garantia de Montagem
      </div>
      <p className="text-sm text-[#334155]">
        Algum problema com a montagem? Você ainda tem <strong>{diasRestantes} dia(s)</strong> para
        solicitar a garantia gratuita. Envie até <strong>5 fotos</strong> do problema e <strong>1 vídeo</strong>.
        Nossa equipe analisa em até <strong>72 horas</strong> para verificar se procede.
      </p>
      {!aberto ? (
        <button onClick={() => setAberto(true)}
          className="mt-4 px-4 py-2 rounded-lg border-[1.5px] border-[#B45309] text-[#B45309] font-semibold text-sm hover:bg-[#B45309] hover:text-white transition-colors"
          data-testid="cliente-garantia-abrir">
          Solicitar garantia gratuita
        </button>
      ) : (
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#334155]">
              Descreva o problema
            </label>
            <Textarea rows={3} value={descricao} onChange={(e) => setDescricao(e.target.value)}
              placeholder="Ex.: a gaveta está agarrando ao fechar…"
              className="mt-1 rounded-lg border-[#CBD5E1] focus-visible:ring-[#FF6700]"
              data-testid="cliente-garantia-descricao" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#334155]">
              Fotos do problema (1 a 5) <span className="text-[#DC2626]">*</span>
            </label>
            <input type="file" accept="image/*" multiple onChange={onFotos}
              className="mt-1 block w-full text-sm text-[#334155] file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#F1F5F9] file:text-[#142766] hover:file:bg-[#E2E8F0]"
              data-testid="cliente-garantia-fotos" />
            {fotos.length > 0 && (
              <div className="mt-1 text-xs text-[#64748B]" data-testid="cliente-garantia-fotos-count">
                {fotos.length} foto(s) selecionada(s)
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#334155]">
              Vídeo mostrando o problema (1) <span className="text-[#DC2626]">*</span>
            </label>
            <input type="file" accept="video/*" onChange={onVideo}
              className="mt-1 block w-full text-sm text-[#334155] file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#F1F5F9] file:text-[#142766] hover:file:bg-[#E2E8F0]"
              data-testid="cliente-garantia-video" />
            {video && (
              <div className="mt-1 text-xs text-[#64748B]" data-testid="cliente-garantia-video-nome">
                {video.name}
              </div>
            )}
          </div>
          <div className="rounded-lg bg-[#FEF3C7] border border-[#FDE68A] p-3 text-xs text-[#92400E]">
            Após o envio, nossa equipe vai analisar as fotos e o vídeo em até <strong>72 horas</strong> para
            verificar se procede. Você receberá o retorno aqui e pelo WhatsApp.
          </div>
          <div className="flex gap-2">
            <button onClick={enviar} disabled={loading}
              className="btn-aj-primary text-sm disabled:opacity-60" data-testid="cliente-garantia-enviar">
              <ShieldCheck size={15} /> {loading ? "Enviando…" : "Enviar para análise"}
            </button>
            <button onClick={() => setAberto(false)} disabled={loading}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-[#64748B] hover:bg-[#F1F5F9] transition-colors"
              data-testid="cliente-garantia-cancelar">
              Voltar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function GarantiaEmAnaliseCard({ solic }) {
  const prazo = solic.prazo_analise ? new Date(solic.prazo_analise) : null;
  const prazoFmt = prazo && !isNaN(prazo)
    ? prazo.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric",
                                        hour: "2-digit", minute: "2-digit" })
    : null;
  return (
    <div className="mt-5 aj-card p-6 border-l-4 border-l-[#F59E0B]" data-testid="cliente-garantia-analise">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#B45309] mb-2">
        <ShieldCheck size={14} /> Solicitação de garantia em análise
      </div>
      <p className="text-sm text-[#334155]">
        Recebemos sua solicitação <span className="font-mono font-bold">{solic.protocolo}</span> com{" "}
        <strong>{solic.fotos_count || 0} foto(s)</strong> e{" "}
        <strong>{solic.video_count ? "1 vídeo" : "vídeo"}</strong>. Nossa equipe está analisando
        para verificar se a garantia procede.
      </p>
      <p className="mt-2 text-sm text-[#334155]">
        <strong>Retorno em até 72 horas.</strong>{prazoFmt ? ` Prazo estimado: ${prazoFmt}.` : ""}
      </p>
      {solic.descricao && (
        <p className="mt-3 text-xs text-[#64748B]">
          Problema relatado: &quot;{solic.descricao}&quot;
        </p>
      )}
    </div>
  );
}

function GarantiaComplementoCard({ solic, onEnviado }) {
  const [fotos, setFotos] = useState([]);
  const [video, setVideo] = useState(null);
  const [descricao, setDescricao] = useState("");
  const [loading, setLoading] = useState(false);

  const onFotos = (e) => {
    const arr = Array.from(e.target.files || []);
    if (arr.length > 5) { toast.error("Envie no máximo 5 fotos"); return; }
    if (arr.some((f) => !(f.type || "").startsWith("image/"))) {
      toast.error("Apenas imagens são aceitas nas fotos"); return;
    }
    setFotos(arr);
  };
  const onVideo = (e) => {
    const f = (e.target.files || [])[0] || null;
    if (f && !(f.type || "").startsWith("video/")) { toast.error("Envie um vídeo (MP4/MOV/WEBM)"); return; }
    setVideo(f);
  };

  const enviar = async () => {
    if (fotos.length === 0 && !video) {
      toast.error("Envie pelo menos 1 nova foto ou 1 vídeo");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      if (descricao.trim()) fd.append("descricao", descricao.trim());
      fotos.forEach((f) => fd.append("fotos", f));
      if (video) fd.append("video", video);
      const r = await axios.post(`${API}/cliente/garantia/solicitacao/${solic.id}/complemento`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(r.data.mensagem || "Evidências enviadas. Análise retomada.");
      setFotos([]); setVideo(null); setDescricao("");
      if (onEnviado) onEnviado();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="mt-5 aj-card p-6 border-l-4 border-l-[#2563EB]" data-testid="cliente-garantia-complemento">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#1D4ED8] mb-2">
        <Camera size={14} /> Garantia · precisamos de mais evidências
      </div>
      <p className="text-sm text-[#334155]">
        Sobre sua solicitação <span className="font-mono font-bold">{solic.protocolo}</span>, nossa
        equipe pediu um complemento antes de decidir:
      </p>
      <div className="mt-2 rounded-lg bg-[#EFF6FF] border border-[#BFDBFE] p-3 text-sm text-[#1E40AF]"
        data-testid="cliente-garantia-complemento-mensagem">
        {solic.complemento_mensagem}
      </div>
      <div className="mt-4 space-y-3">
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-[#334155]">Novas fotos (até 5)</label>
          <input type="file" accept="image/*" multiple onChange={onFotos}
            className="mt-1 block w-full text-sm text-[#334155] file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#F1F5F9] file:text-[#142766] hover:file:bg-[#E2E8F0]"
            data-testid="cliente-garantia-complemento-fotos" />
          {fotos.length > 0 && (
            <div className="mt-1 text-xs text-[#64748B]">{fotos.length} foto(s) selecionada(s)</div>
          )}
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-[#334155]">Novo vídeo (opcional)</label>
          <input type="file" accept="video/*" onChange={onVideo}
            className="mt-1 block w-full text-sm text-[#334155] file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#F1F5F9] file:text-[#142766] hover:file:bg-[#E2E8F0]"
            data-testid="cliente-garantia-complemento-video" />
          {video && <div className="mt-1 text-xs text-[#64748B]">{video.name}</div>}
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-[#334155]">Comentário (opcional)</label>
          <Textarea rows={2} value={descricao} onChange={(e) => setDescricao(e.target.value)}
            placeholder="Alguma informação extra sobre as novas evidências…"
            className="mt-1 rounded-lg border-[#CBD5E1] focus-visible:ring-[#FF6700]"
            data-testid="cliente-garantia-complemento-descricao" />
        </div>
        <div className="rounded-lg bg-[#EFF6FF] border border-[#BFDBFE] p-3 text-xs text-[#1E40AF]">
          Após o reenvio, o prazo de análise de <strong>72 horas</strong> recomeça.
        </div>
        <button onClick={enviar} disabled={loading}
          className="btn-aj-primary text-sm disabled:opacity-60" data-testid="cliente-garantia-complemento-enviar">
          <Camera size={15} /> {loading ? "Enviando…" : "Reenviar evidências"}
        </button>
      </div>
    </div>
  );
}

function GarantiaRecusadaCard({ recusa }) {
  const irParaChat = () => {
    document.querySelector('[data-testid="cliente-chat-wrap"]')?.scrollIntoView({ behavior: "smooth" });
  };
  const quando = recusa.decidido_em
    ? new Date(recusa.decidido_em).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" })
    : null;
  return (
    <div className="mt-5 aj-card p-6 border-l-4 border-l-[#DC2626]" data-testid="cliente-garantia-recusada">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#B91C1C] mb-2">
        <XCircle size={14} /> Solicitação de garantia não aprovada
      </div>
      <p className="text-sm text-[#334155]">
        Analisamos as evidências da solicitação <span className="font-mono font-bold">{recusa.protocolo}</span>
        {quando ? ` em ${quando}` : ""} e, neste caso, a garantia de montagem não se aplica.
      </p>
      <div className="mt-3 rounded-lg bg-[#FEF2F2] border border-[#FECACA] p-3" data-testid="cliente-garantia-recusa-motivo">
        <div className="text-xs font-bold uppercase tracking-wider text-[#991B1B]">Motivo</div>
        <div className="text-sm text-[#7F1D1D] font-semibold mt-0.5">{recusa.motivo_label || "—"}</div>
        {recusa.observacao_recusa && (
          <div className="text-xs text-[#991B1B] mt-1">{recusa.observacao_recusa}</div>
        )}
      </div>
      <p className="mt-3 text-sm text-[#334155]">
        Não concorda ou precisa de outro tipo de atendimento? Abra um chamado alternativo com nossa equipe:
      </p>
      <div className="mt-3 flex flex-col sm:flex-row gap-2">
        <button onClick={irParaChat}
          className="btn-aj-primary text-sm" data-testid="cliente-garantia-recusa-chat">
          Falar com a equipe pelo chat
        </button>
        <a href="https://wa.me/5511939387366" target="_blank" rel="noreferrer"
          className="px-4 py-2 rounded-lg border-[1.5px] border-[#16A34A] text-[#15803D] font-semibold text-sm text-center hover:bg-[#16A34A] hover:text-white transition-colors"
          data-testid="cliente-garantia-recusa-whatsapp">
          WhatsApp da central (11) 93938-7366
        </a>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" });
}
